import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
    Grid,
    Typography,
    Alert,
    CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { MapContainer, TileLayer, useMap, FeatureGroup, Polygon, Popup as LeafletPopup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import L from "leaflet";
import { EditControl } from 'react-leaflet-draw';
import { useObtenerNapsRed, useObtenerNaps } from '@/shared/actions/erp/cobertura-naps/cobertura-naps.actions';
import LocateControl from './LocateControl';
import NapsSearchControl, { NapOption } from './NapsSearchControl';

const ChangeMapView: React.FC<{ center: [number, number] }> = ({ center }) => {
    const map = useMap();
    React.useEffect(() => {
        if (map) {
            map.setView(center, map.getZoom());
        }
    }, [center, map]);
    return null;
};

const SearchControl = () => {
    const map = useMap();
    const markerRef = useRef<L.Marker | null>(null);

    useEffect(() => {
        const geocoder = (L.Control as any).Geocoder.nominatim();
        const control = (L.Control as any).geocoder({
            geocoder,
            defaultMarkGeocode: false,
        }).on("markgeocode", function (e: any) {
            const latlng = e.geocode.center;
            if (markerRef.current) {
                map.removeLayer(markerRef.current);
            }
            const newMarker = L.marker(latlng).addTo(map);
            markerRef.current = newMarker;
            map.setView(latlng, 10);
        });

        control.addTo(map);

        return () => {
            control.remove();
            if (markerRef.current) {
                map.removeLayer(markerRef.current);
            }
        };
    }, [map]);

    return null;
};

interface NapPointProps {
    napKey: string | number;
    lat: number;
    lng: number;
    isSelected: boolean;
    nombre?: string;
    ciudad?: string;
    puertosOc?: number | string;
    soterrada?: string;
    registerRef: (key: string | number, layer: any) => void;
    onSelect: (key: string | number) => void;
}

const NapPoint = React.memo(({
    napKey,
    lat,
    lng,
    isSelected,
    nombre,
    ciudad,
    puertosOc,
    soterrada,
    registerRef,
    onSelect,
}: NapPointProps) => {
    if (lat == null || lng == null || (lat === 0 && lng === 0)) return null;

    const sizeMeters = 10;
    const dLat = sizeMeters / 111320;
    const dLng = sizeMeters / (111320 * Math.cos(lat * Math.PI / 180));

    const triangle: [number, number][] = [
        [lat + dLat * 2, lng],
        [lat - dLat, lng - dLng],
        [lat - dLat, lng + dLng],
    ];

    const esConjunto = soterrada === 'SI';

    return (
        <Polygon
            ref={(el: any) => {
                if (el) registerRef(napKey, el);
            }}
            positions={triangle}
            pathOptions={isSelected
                ? {
                    color: '#e67e22',
                    fillColor: '#f39c12',
                    fillOpacity: 1,
                    weight: 3,
                }
                : {
                    color: '#1b5d8a',
                    fillColor: '#1b5d8a',
                    fillOpacity: 0.7,
                    weight: 1.6,
                }}
            eventHandlers={{
                click: () => onSelect(napKey),
            }}
        >
            <LeafletPopup>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Typography variant='subtitle1'>
                            {nombre}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='subtitle2'>
                            # Puertos ocupados: {puertosOc}
                        </Typography>
                    </Grid>

                    {esConjunto && (
                        <Grid item xs={12}>
                            <Typography variant='subtitle2'>
                                Conjunto: Sí
                            </Typography>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Typography variant='subtitle2'>
                            Ciudad: {ciudad}
                        </Typography>
                    </Grid>
                </Grid>
            </LeafletPopup>
        </Polygon>
    );
});

export default function NapsRed() {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [centerMap] = useState<[number, number]>([-0.3208730168341767, -78.47201823285198])

    const obtenerNapsRed = useObtenerNapsRed();
    const obtenerNaps = useObtenerNaps();

    useEffect(() => {
        const LAny = L as any;

        if (!LAny.drawLocal) return;

        LAny.drawLocal.draw.toolbar.buttons.polyline = 'Dibujar línea';
        LAny.drawLocal.draw.toolbar.actions.text = 'Cancelar';
        LAny.drawLocal.draw.toolbar.finish.text = 'Finalizar';
        LAny.drawLocal.draw.toolbar.undo.text = 'Deshacer';

        LAny.drawLocal.draw.handlers.polyline.tooltip.start = 'Haz clic para comenzar';
        LAny.drawLocal.draw.handlers.polyline.tooltip.cont = 'Haz clic para continuar';
        LAny.drawLocal.draw.handlers.polyline.tooltip.end = 'Haz clic para terminar';

        // Botones principales
        LAny.drawLocal.edit.toolbar.buttons.edit = 'Editar elementos';
        LAny.drawLocal.edit.toolbar.buttons.remove = 'Eliminar elementos';
        LAny.drawLocal.edit.toolbar.actions.save.text = 'Guardar';
        LAny.drawLocal.edit.toolbar.actions.cancel.text = 'Cancelar';
        LAny.drawLocal.edit.toolbar.actions.clearAll.text = 'Eliminar todo';
        LAny.drawLocal.edit.toolbar.actions.clearAll.title = 'Eliminar todos los elementos';
        LAny.drawLocal.edit.handlers.edit.tooltip.text = 'Arrastra los puntos para editar';
        LAny.drawLocal.edit.handlers.edit.tooltip.subtext = 'Haz clic en cancelar para deshacer cambios';
        LAny.drawLocal.edit.handlers.remove.tooltip.text = 'Haz clic sobre un elemento para eliminar';
        LAny.drawLocal.edit.toolbar.buttons.editDisabled = 'No hay elementos para editar';
        LAny.drawLocal.edit.toolbar.buttons.removeDisabled = 'No hay elementos para eliminar';
    }, []);

    const [datosnaps, setDatosNaps] = useState<any>(null);
    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [red, puntos] = await Promise.all([
                obtenerNapsRed.mutateAsync([]),
                obtenerNaps.mutateAsync([]),
            ]);

            const data: any = red?.data;
            const features: any[] = data?.features || data;
            if (Array.isArray(features)) {
                const tipos = features.map((f: any) => f?.geometry?.type);
                const conteo: Record<string, number> = {};
                tipos.forEach((t: string) => { conteo[t] = (conteo[t] || 0) + 1; });
            } else {
                console.warn('[CoberturaNaps] respuesta sin features:', data);
            }

            setDatosNaps(features);
            setDatosNapsPuntos(puntos?.data ?? null);
        } catch (err: any) {
            toast.error(err?.message || "Error al cargar datos");
        } finally {
            requestAnimationFrame(() => {
                setTimeout(() => setIsLoading(false), 50);
            });
        }
    }, [obtenerNapsRed, obtenerNaps]);

    const didLoad = useRef(false);

    useEffect(() => {
        if (didLoad.current) return;
        didLoad.current = true;
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [datosNapsPuntos, setDatosNapsPuntos] = useState<any>(null);
    const napLayerRefs = useRef<Record<string, any>>({});
    const [selectedNapId, setSelectedNapId] = useState<string | number | null>(null);

    const napOptions = useMemo<NapOption[]>(() => {
        if (!datosNapsPuntos?.features) return [];
        const options: NapOption[] = [];
        datosNapsPuntos.features.forEach((feature: any) => {
            const nombre = feature?.properties?.nombre;
            const coords = feature?.geometry?.coordinates;
            if (!nombre || !Array.isArray(coords) || !coords.length || !Array.isArray(coords[0])) return;
            const [lng, lat] = coords[0];
            if (lat == null || lng == null || (lat === 0 && lng === 0)) return;
            options.push({
                id: feature.id ?? nombre,
                nombre,
                ciudad: feature?.properties?.ciudad,
                lat,
                lng,
            });
        });
        return options;
    }, [datosNapsPuntos]);

    const handleSelectNap = useCallback((nap: NapOption) => {
        setSelectedNapId(nap.id);
        const layer = napLayerRefs.current[nap.id];
        layer?.openPopup();
    }, []);

    const registerNapLayerRef = useCallback((key: string | number, layer: any) => {
        if (layer && !napLayerRefs.current[key]) {
            napLayerRefs.current[key] = layer;
        }
    }, []);

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ bgcolor: "info.light" }}>
                        <Typography variant="body1">
                            <strong>Nota:</strong> Se muestra la cobertura de QGIS, de la capa (Cobertura NAPS)
                        </Typography>
                    </Alert>
                </Grid>
                <Grid item xs={12}>
                    <div style={{ position: "relative" }}>

                        {isLoading && (
                            <div style={{
                                position: "absolute",
                                zIndex: 1000,
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 8,
                                background: "rgba(255,255,255,0.6)",
                                pointerEvents: "all",
                                cursor: "wait",
                            }}>
                                <CircularProgress />
                                <Typography variant="body1">Cargando mapa...</Typography>
                            </div>
                        )}
                        <MapContainer center={[0, 0]} zoom={11} preferCanvas style={{ height: '90vh', width: '100%' }}>
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            <SearchControl />
                            <LocateControl />
                            <NapsSearchControl naps={napOptions} onSelect={handleSelectNap} />

                            <FeatureGroup>
                                <EditControl
                                    position="topright"
                                    onCreated={(e: any) => {
                                        const layer = e.layer;
                                        if (e.layerType === 'polyline') {
                                            const latlngs = layer.getLatLngs();

                                            let totalDistance = 0;
                                            for (let i = 0; i < latlngs.length - 1; i++) {
                                                totalDistance += latlngs[i].distanceTo(latlngs[i + 1]);
                                            }
                                            const km = (totalDistance / 1000).toFixed(2);
                                            layer.bindPopup(`Distancia: ${km} km`).openPopup();

                                            layer.setStyle({
                                                color: 'red',
                                                weight: 4,
                                            });
                                        }
                                    }}
                                    draw={{
                                        rectangle: false,
                                        polygon: false,
                                        circle: false,
                                        marker: false,
                                        circlemarker: false,
                                        polyline: true,
                                    }}
                                    edit={{
                                        featureGroup: undefined,
                                        edit: {
                                            selectedPathOptions: {
                                                maintainColor: false,
                                                color: 'red',
                                                opacity: 0.8,
                                            },
                                        },
                                        remove: true,
                                    }}
                                />
                            </FeatureGroup>

                            {useMemo(() => Array.isArray(datosnaps) && datosnaps.map((feature: any, featureIdx: number) => {
                                const geometry = feature.geometry;
                                if (!geometry) return null;
                                const keyBase = feature.id ?? featureIdx;

                                const renderPoint = (coords: number[], pointKey: string | number) => {
                                    if (!Array.isArray(coords) || coords.length < 2) return null;
                                    const [lng, lat] = coords;
                                    if (lat == null || lng == null || (lat === 0 && lng === 0)) return null;

                                    const sizeMeters = 10;
                                    const dLat = sizeMeters / 111320;
                                    const dLng = sizeMeters / (111320 * Math.cos(lat * Math.PI / 180));

                                    const triangle: [number, number][] = [
                                        [lat + dLat * 2, lng],
                                        [lat - dLat, lng - dLng],
                                        [lat - dLat, lng + dLng],
                                    ];

                                    return (
                                        <Polygon
                                            key={pointKey}
                                            positions={triangle}
                                            pathOptions={{ color: '#c0392b', fillColor: '#e74c3c', fillOpacity: 0.8, weight: 1.5 }}
                                        >
                                        </Polygon>
                                    );
                                };

                                const renderPolygonCoords = (coords: number[][][], polyKey: string | number) => {
                                    const positions: [number, number][][] = coords.map(ring =>
                                        ring.map(([lng, lat]: number[]) => [lat, lng] as [number, number])
                                    );
                                    const hasValidCoord = positions.some(ring =>
                                        ring.some(([lat, lng]) => lat !== 0 || lng !== 0)
                                    );
                                    if (!hasValidCoord) return null;
                                    return (
                                        <Polygon
                                            key={polyKey}
                                            positions={positions}
                                            pathOptions={{ color: '#c0392b', fillColor: '#e74c3c', fillOpacity: 0.3, weight: 1.5 }}
                                        >
                                        </Polygon>
                                    );
                                };

                                if (geometry.type === "Point") {
                                    return renderPoint(geometry.coordinates, keyBase);
                                }

                                if (geometry.type === "MultiPoint") {
                                    return geometry.coordinates.map((pt: number[], i: number) =>
                                        renderPoint(pt, `${keyBase}-mp-${i}`)
                                    );
                                }

                                if (geometry.type === "Polygon") {
                                    return renderPolygonCoords(geometry.coordinates, keyBase);
                                }

                                if (geometry.type === "MultiPolygon") {
                                    return geometry.coordinates.map((polyCoords: number[][][], i: number) =>
                                        renderPolygonCoords(polyCoords, `${keyBase}-mpoly-${i}`)
                                    );
                                }

                                return null;
                            }), [datosnaps])}

                            {datosNapsPuntos?.features?.map((feature: any) => {
                                const geometry = feature.geometry;
                                if (!geometry) return null;
                                if (geometry.type !== "MultiPoint") return null;

                                const napKey = feature.id ?? feature.properties.nombre;

                                return geometry.coordinates.map((pt: any, index: number) => {
                                    if (!Array.isArray(pt) || pt.length < 2) {
                                        return null;
                                    }
                                    return (
                                        <NapPoint
                                            key={`${feature.id}-${index}`}
                                            napKey={napKey}
                                            lat={pt[1]}
                                            lng={pt[0]}
                                            isSelected={selectedNapId === napKey}
                                            nombre={feature.properties?.nombre}
                                            ciudad={feature.properties?.ciudad}
                                            puertosOc={feature.properties?.puertos_oc}
                                            soterrada={feature.properties?.Soterrada}
                                            registerRef={registerNapLayerRef}
                                            onSelect={setSelectedNapId}
                                        />
                                    );
                                });
                            })}

                            <ChangeMapView center={centerMap} />
                        </MapContainer>
                    </div>
                </Grid>
            </Grid>
        </>
    );
}
